import moment from "moment";
import { nanoid } from "nanoid";
import { formatPrice } from "../../../utils/formatPrice";
import { Work, CreditCard, Score } from "@mui/icons-material";
import { ExpandMore, Person, Info } from "@mui/icons-material";
import { convertToFormattedAmount } from "../../../utils/convertAmount";
import type { CreditConsentInterface } from "../../../interfaces/mortgageDetailsInterface";
import {
  Box,
  List,
  Table,
  Paper,
  ListItem,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Accordion,
  Typography,
  ListItemIcon,
  ListItemText,
  TableContainer,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";

interface CreditConsentSectionProps {
  result: CreditConsentInterface;
}

export const CreditConsentSection = ({ result }: CreditConsentSectionProps) => {
  return (
    <Paper
      key={result.firstName + nanoid()}
      elevation={4}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
      }}
    >
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Typography variant="h5" color="primary" fontWeight="bold">
          Credit Consent
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {result.firstName} {result.middleName} {result.lastName}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
          flexDirection: { xs: "column", sm: "row" }
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            backgroundColor: "rgba(25, 118, 210, 0.05)",
            p: 2,
            borderRadius: "8px"
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Credit Score
          </Typography>
          <Typography
            variant="h4"
            color="primary"
            fontWeight="bold"
            sx={{ mt: 1 }}
          >
            {result.creditScore ? result.creditScore : "N/A"}
          </Typography>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            backgroundColor: "rgba(25, 118, 210, 0.05)",
            p: 2,
            borderRadius: "8px"
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Total Monthly Debt
            <Box sx={{ color: "#FECD07", fontSize: 12 }}>
              (Joint Acc. Included)
            </Box>
          </Typography>
          <Typography
            variant="h4"
            color="primary"
            fontWeight="bold"
            sx={{ mt: 1 }}
          >
            {result.totalMonthlyDebt
              ? `$${formatPrice(convertToFormattedAmount(result.totalMonthlyDebt.amount))}`
              : "N/A"}
          </Typography>
        </Box>
        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            backgroundColor: "rgba(25, 118, 210, 0.05)",
            p: 2,
            borderRadius: "8px"
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Total Debt
            <Box sx={{ color: "#FECD07", fontSize: 12 }}>
              (Joint Acc. Included)
            </Box>
          </Typography>
          <Typography
            variant="h4"
            color="primary"
            fontWeight="bold"
            sx={{ mt: 1 }}
          >
            {result.totalDebt
              ? `$${formatPrice(convertToFormattedAmount(result.totalDebt.amount))}`
              : "N/A"}
          </Typography>
        </Box>
      </Box>
      {result.creditTradeLines?.length && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            color="primary"
            fontWeight="bold"
            gutterBottom
          >
            Accounts
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              maxHeight: "800px",
              overflowY: "auto"
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    textAlign: "center",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#292929"
                  }}
                >
                  {[
                    "Creditor",
                    "Creditor Number",
                    "Creditor Phone",
                    "Account Number",
                    "Account Type",
                    "Ownership Type",
                    "Credit Limit",
                    "Current Balance",
                    "Monthly Payment",
                    "Past Due Amount",
                    "Months Reported",
                    "Opened On",
                    "Last Activity On",
                    "Reported On",
                    "Payment Status"
                  ].map((header) => (
                    <TableCell key={header}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.creditTradeLines.map((creditTradeLine, idx) => (
                  <TableRow
                    key={creditTradeLine.accountNumber || idx}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.05)"
                      }
                    }}
                  >
                    <TableCell>
                      {creditTradeLine.creditorName
                        ? creditTradeLine.creditorName
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.creditorNumber
                        ? creditTradeLine.creditorNumber
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.creditorPhone
                        ? creditTradeLine.creditorPhone
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.accountNumber
                        ? creditTradeLine.accountNumber
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.accountType
                        ? creditTradeLine.accountType
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.ownershipType
                        ? creditTradeLine.ownershipType
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.creditLimit
                        ? `$${formatPrice(convertToFormattedAmount(creditTradeLine.creditLimit.amount))}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.currentBalance
                        ? `$${formatPrice(convertToFormattedAmount(creditTradeLine.currentBalance.amount))}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.monthlyPayment
                        ? `$${formatPrice(convertToFormattedAmount(creditTradeLine.monthlyPayment.amount))}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.pastDueAmount
                        ? `$${formatPrice(convertToFormattedAmount(creditTradeLine.pastDueAmount.amount))}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.monthsReported
                        ? creditTradeLine.monthsReported
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.openedOn
                        ? moment(creditTradeLine.openedOn).format("YYYY-MM")
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.lastActivityOn
                        ? moment(creditTradeLine.lastActivityOn).format(
                            "YYYY-MM"
                          )
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.reportedOn
                        ? moment(creditTradeLine.reportedOn).format("YYYY-MM")
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {creditTradeLine.paymentStatus
                        ? creditTradeLine.paymentStatus
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          color="primary"
          fontWeight="bold"
          gutterBottom
          sx={{ textAlign: "center" }}
        >
          Credit Report Summary
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between"
          }}
        >
          <Box
            sx={{
              flex: "1 1 calc(50% - 16px)",
              minWidth: "300px",
              p: 2,
              borderRadius: "8px"
            }}
          >
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" fontWeight="bold">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Person color="primary" /> Personal Information
                  </Box>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mt: 1 }}>
                  <strong>First Name:</strong> {result.firstName || "N/A"}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  <strong>Middle Name:</strong> {result.middleName || "N/A"}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  <strong>Last Name:</strong> {result.lastName || "N/A"}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>

          {result.employmentHistory?.length &&
            result.employmentHistory?.length > 0 && (
              <Box
                sx={{
                  flex: "1 1 calc(50% - 16px)",
                  minWidth: "300px",
                  p: 2,
                  borderRadius: "8px"
                }}
              >
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Work color="primary" /> Employment Details
                      </Box>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Employer Name
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Occupation
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Description
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.employmentHistory.map((job) => (
                            <TableRow key={nanoid()}>
                              <TableCell>{job.name || "N/A"}</TableCell>
                              <TableCell>{job.occupation || "N/A"}</TableCell>
                              <TableCell>{job.description || "N/A"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          {result.creditInquiries?.length &&
            result.creditInquiries?.length > 0 && (
              <Box
                sx={{
                  flex: "1 1 calc(50% - 16px)",
                  minWidth: "300px",
                  p: 2,
                  borderRadius: "8px"
                }}
              >
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CreditCard color="primary" /> Credit Inquiry Details
                      </Box>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Name
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Date
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.creditInquiries.map((inquiry) => (
                            <TableRow key={nanoid()}>
                              <TableCell>{inquiry.name || "N/A"}</TableCell>
                              <TableCell>
                                {inquiry.createdAt
                                  ? moment(inquiry.createdAt).format(
                                      "YYYY-MM-DD"
                                    )
                                  : "N/A"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          {result.creditScoreFactors?.length &&
            result.creditScoreFactors?.length > 0 && (
              <Box
                sx={{
                  flex: "1 1 calc(50% - 16px)",
                  minWidth: "300px",
                  p: 2,
                  borderRadius: "8px"
                }}
              >
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" fontWeight="bold">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Score color="primary" /> Factors Affecting Your Credit
                        Score
                      </Box>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {result.creditScoreFactors.map((factor) => (
                        <ListItem key={nanoid()}>
                          <ListItemIcon>
                            <Info color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={factor} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
        </Box>
      </Box>
    </Paper>
  );
};
